import {
  type Column,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnSizingState,
  type FilterFn,
  type OnChangeFn,
  type PaginationState,
  type Row,
  type SortingFn,
  type SortingState,
  type VisibilityState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Columns3,
  Download,
  Filter,
  GripVertical,
  Loader2,
  RefreshCw,
  X,
} from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";
import { toast as sonnerToast } from "sonner";
import * as XLSX from "xlsx";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ListLoadingOverlay } from "@/components/ui/list-loading-overlay";
import { toast } from "@/components/ui/sonner";

import { cn, getQueryErrorMessage, parseDateAsLocal } from "@/lib/utils";
import { format } from "date-fns";
import { Checkbox } from "../ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "../ui/command";
import { DatePicker } from "../ui/date-picker";
import { FilterSearchInput } from "../ui/filter-search-input";
import { ColumnPresenceFilterChoices } from "./column-presence-filter";
import { DashboardColumnFilterBadges } from "./dashboard-column-filter-badges";
import {
  EMPTY_FILTER_VALUE,
  NOT_BLANK_FILTER_VALUE,
  displayValueInList,
  displayValueMatchesFilters,
  displayValuesEqual,
  encodeContainsFilterValue,
  withoutPresenceFilterValues,
} from "./dashboard-column-filter-match";
import {
  type ColumnFilterBadge,
  buildColumnFilterBadges,
  removeColumnFilterBadge,
} from "./dashboard-column-filter-utils";
import {
  buildDateRangeFilterValue,
  defaultDateFilterValue,
  matchesDateRangeFilter,
  parseDateRangeFilterValue,
  resolveColumnFilterType,
} from "./dashboard-table-date-filter";
import { nextFilterPanelPosition } from "./dashboard-table-filter-panel-position";

// Generic types
export type ColumnProperties<T, K extends string> = {
  displayName: string;
  defaultVisible: boolean;
  getValue: (row: T, accessorKey: K) => string;
  sortingFn?: (rowA: Row<T>, rowB: Row<T>, columnId: K) => number;
  renderCell?: (row: T) => React.ReactNode;
  /** If false, the column filter popover is hidden. Default true. */
  filterable?: boolean;
  /**
   * When provided, these values are used as the filter options instead of
   * deriving them from the table's faceted unique values. Useful for columns
   * where the full set of options should be available even when some have no
   * matching rows (e.g. license types that have no jurisdictions configured).
   */
  filterValues?: string[];
  /** Use checkbox value filters (default) or from/to date range pickers. */
  filterType?: "values" | "dateRange";
  /**
   * When false, Blank and Not blank are hidden because every row has a value.
   * Defaults to true.
   */
  allowBlankFilter?: boolean;
  /**
   * Raw value used for date-range filtering. Defaults to the row field matching
   * the accessor key, then falls back to `getValue`.
   */
  getFilterValue?: (row: T, accessorKey: string) => string;
  /** Override auto-estimated default column width in pixels. */
  defaultWidth?: number;
  /**
   * When set, this decides whether a row stays visible for the column filter.
   * Use it when the header filter is broader than an exact cell match.
   */
  rowMatchesFilter?: (row: T, filterValues: string[]) => boolean;
};

// Generic table component
interface DashboardTableProps<T, K extends string> {
  data: T[];
  onRowClick?: (rowId: string) => void;
  columnVisibility?: Record<K, boolean>;
  onColumnVisibilityChange?: (visibility: Record<K, boolean>) => void;
  // Column filters
  columnFilters?: Partial<Record<K, string[]>>;
  onColumnFiltersChange?: (filters: Record<K, string[]>) => void;
  /** Used only when neither controlled nor persisted filters exist. */
  defaultColumnFilters?: Partial<Record<K, string[]>>;
  // Generic props
  accessorKeys: readonly K[];
  getColumnProperties: (accessorKey: K) => ColumnProperties<T, K>;
  /**
   * Optional export-specific column resolver. Use when displayed cells intentionally
   * hide values in the UI (e.g. fee masking), but exports should include full values.
   */
  getExportColumnProperties?: (
    accessorKey: K,
    mode: ExportColumnMode,
  ) => ColumnProperties<T, K>;
  getRowId: (row: T) => string;
  // Initial sort props
  initialSortColumn?: K;
  initialSortDirection?: "asc" | "desc";
  /** When false, rows keep `data` order (no sort UI). Default true. */
  enableSorting?: boolean;
  /** When false, column filter popovers are hidden. Default true. */
  enableColumnFilters?: boolean;
  /** First column (left): e.g. reorder controls. Does not affect export. */
  renderRowLeadingActions?: (row: T) => React.ReactNode;
  /** Header label for the leading column (default empty). */
  leadingActionsHeaderLabel?: string;
  // Custom row actions
  renderRowActions?: (row: T) => React.ReactNode;
  /** Sticky actions column width in pixels. Default 240. */
  rowActionsColumnWidth?: number;
  // Custom controls
  customControls?: React.ReactNode;
  /** Rendered before the Export button in the table toolbar. */
  leadingToolbarControls?: React.ReactNode;
  /** Rendered in the table footer, immediately left of the rows-per-page control. */
  footerLeadingControls?: React.ReactNode;
  /** When true, defer expensive row-model derivations during active stream updates. */
  optimizeForStreaming?: boolean;
  /**
   * Appended to export columns even when hidden (e.g. optional detail columns that
   * should still appear in CSV/XLSX).
   */
  alwaysIncludeInExportKeys?: readonly K[];
  /**
   * Storage key namespace for persisted table layout (order, visibility, widths,
   * page size). It must not vary with the accessor keys: dynamic columns (e.g.
   * question-backed list columns) change `accessorKeys` after mount, and a key
   * that moved with them would read one layout and save another.
   *
   * Pass a distinct key for every table that can share a route pathname, and a
   * distinct key per list variant that should keep its own layout (e.g. active
   * vs inactive registrations). If omitted, the route pathname is used, which
   * only holds for a route with a single table.
   */
  tablePersistenceKey?: string;
  /**
   * Server-driven paging, sorting, and filtering. The parent owns `data` as the
   * current page and `rowCount` as the filtered universe.
   */
  serverMode?: boolean;
  rowCount?: number;
  pageIndex?: number;
  pageSize?: number;
  onPaginationChange?: (pagination: PaginationState) => void;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /**
   * Page-style list layout: the GUI above the rows scrolls vertically with
   * sticky column titles, horizontal scrolling applies only to the table,
   * the horizontal scrollbar stays at the bottom of the pane, and the
   * "Showing rows" / Previous / Next footer stays visible.
   */
  lockToViewport?: boolean;
  /**
   * Hide the duplicate Rows per page / Page X of Y / Previous / Next controls
   * above the list. The footer still shows them.
   */
  hideTopPagination?: boolean;
  /** Rows-per-page choices in the pagination control. */
  pageSizeOptions?: readonly number[];
  /** Rendered inside the vertical scroller, above the table toolbar. */
  leadingContent?: React.ReactNode;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  refreshAriaLabel?: string;
  dataUpdatedAt?: number;
  onFilterPopoverOpen?: (columnId: string, search?: string) => void;
  onServerExport?: (
    mode: ExportColumnMode,
    format: "csv" | "excel",
  ) => void | Promise<void>;
  /**
   * Filtered and sorted rows in table order, ignoring pagination.
   * Used for next/previous navigation through the currently shown list.
   */
  onVisibleRowsChange?: (rows: T[]) => void;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const LEADING_ACTIONS_COLUMN_WIDTH = 48;
const ROW_ACTIONS_COLUMN_WIDTH = 240;
// Column drag auto-scroll: how far inside each edge the pointer starts
// scrolling, and how fast it scrolls once the pointer is past the edge.
const COLUMN_DRAG_EDGE_ZONE_PX = 72;
const COLUMN_DRAG_MAX_SCROLL_PX_PER_FRAME = 24;
export const FILTER_VALUE_SEARCH_DEBOUNCE_MS = 300;
// Opaque bg required: translucent header tint lets other column titles show
// through when this sticky-right header covers them during horizontal scroll.
const STICKY_ACTIONS_HEADER_CLASS =
  "sticky right-0 z-30 p-2 text-left text-xs border bg-muted text-card-foreground shadow-[-1px_0_0_0_hsl(var(--border))]";
const STICKY_ACTIONS_CELL_CLASS =
  "sticky right-0 z-20 text-nowrap px-2 py-2 text-xs border-r bg-card group-hover:bg-muted shadow-[-1px_0_0_0_hsl(var(--border))]";

/**
 * Keep persisted column order, but insert any new accessor keys at their
 * natural positions so the table default matches the Columns dropdown order.
 *
 * Persisted ids that are not (yet) in accessorKeys are kept in place rather
 * than dropped: dynamic columns (e.g. question-backed license-list columns)
 * load asynchronously after the table mounts, and dropping their ids here
 * would erase their saved position before they exist. Unknown ids are
 * harmless — the table and the Columns dropdown ignore them.
 */
/**
 * Persist column order across dynamic accessor-key changes without writing a
 * new array when the merged order is identical (avoids React #185 loops when
 * parents pass unstable `accessorKeys` references).
 */
function sameStringOrder(
  left: readonly string[],
  right: readonly string[],
): boolean {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    if (left[i] !== right[i]) return false;
  }
  return true;
}

/**
 * Neighbor to swap with while dragging a column horizontally.
 * Only the immediate neighbor qualifies, and only once the pointer passes
 * its midpoint. Entering the edge of a header is not enough: with uneven
 * widths that jumps the dragged column past the target, the row shifts
 * under the pointer, and the order snaps back.
 */
export function columnDragNeighborId(
  headers: readonly { id: string; left: number; width: number }[],
  draggingId: string,
  clientX: number,
): string | null {
  const fromIndex = headers.findIndex((header) => header.id === draggingId);
  if (fromIndex < 0) return null;
  const next = headers[fromIndex + 1];
  if (next && next.width > 0 && clientX > next.left + next.width / 2) {
    return next.id;
  }
  const prev = headers[fromIndex - 1];
  if (prev && prev.width > 0 && clientX < prev.left + prev.width / 2) {
    return prev.id;
  }
  return null;
}

/**
 * Move `draggingId` one step through the visible header order. Hidden ids
 * stay in the slots between visible columns.
 */
export function reorderColumnByVisibleNeighbor(
  order: readonly string[],
  visibleIds: readonly string[],
  draggingId: string,
  neighborId: string,
): string[] {
  const fromVis = visibleIds.indexOf(draggingId);
  const toVis = visibleIds.indexOf(neighborId);
  if (fromVis < 0 || toVis < 0 || Math.abs(fromVis - toVis) !== 1) {
    return [...order];
  }
  const nextVisible = visibleIds.slice();
  nextVisible[fromVis] = neighborId;
  nextVisible[toVis] = draggingId;
  const visibleSet = new Set(visibleIds);
  let visibleIndex = 0;
  return order.map((id) => {
    if (!visibleSet.has(id)) return id;
    const replacement = nextVisible[visibleIndex];
    visibleIndex += 1;
    return replacement ?? id;
  });
}

function leafColumnHeaderCells(list: HTMLElement): HTMLElement[] {
  const rows = [...list.querySelectorAll<HTMLTableRowElement>("thead > tr")];
  let best: HTMLElement[] = [];
  for (const row of rows) {
    const cells = [...row.children].filter(
      (cell): cell is HTMLElement =>
        cell instanceof HTMLElement &&
        cell.tagName === "TH" &&
        Boolean(cell.dataset.columnId),
    );
    if (cells.length > best.length) best = cells;
  }
  return best;
}

function columnFiltersStateEqual(
  left: ColumnFiltersState,
  right: ColumnFiltersState,
): boolean {
  if (left.length !== right.length) return false;
  const rightById = new Map(
    right.map((filter) => [filter.id, filter.value as string[]]),
  );
  for (const filter of left) {
    const nextValues = rightById.get(filter.id);
    if (!nextValues) return false;
    if (!sameStringOrder(filter.value as string[], nextValues)) return false;
  }
  return true;
}

export function mergeColumnOrderWithAccessorKeys<K extends string>(
  persistedOrder: readonly string[],
  accessorKeys: readonly K[],
): string[] {
  if (persistedOrder.length === 0) {
    return [...accessorKeys];
  }

  const result: string[] = [...persistedOrder];

  for (const key of accessorKeys) {
    if (result.includes(key)) continue;

    const keyIndex = accessorKeys.indexOf(key);
    let insertAt = result.length;

    for (let i = keyIndex - 1; i >= 0; i -= 1) {
      const previousKey = accessorKeys[i];
      if (previousKey === undefined) continue;
      const prevPos = result.indexOf(previousKey);
      if (prevPos >= 0) {
        insertAt = prevPos + 1;
        break;
      }
    }

    if (insertAt === result.length) {
      for (let i = keyIndex + 1; i < accessorKeys.length; i += 1) {
        const nextKey = accessorKeys[i];
        if (nextKey === undefined) continue;
        const nextPos = result.indexOf(nextKey);
        if (nextPos >= 0) {
          insertAt = nextPos;
          break;
        }
      }
    }

    result.splice(insertAt, 0, key);
  }

  return result;
}

/** Column ids that show a property address on list tables. */
const PROPERTY_ADDRESS_COLUMN_KEYS = new Set<string>([
  "property.address1",
  "property_address",
  "property_address_full",
]);

/**
 * Bump when the default property-address position changes. Saved layouts are
 * adjusted once so an existing column order picks up the new default.
 */
export const PROPERTY_ADDRESS_COLUMN_LAYOUT_VERSION = 1;

/**
 * Move the property-address column to sit immediately after the column that
 * precedes it in `accessorKeys`. Other saved positions stay put.
 */
export function placePropertyAddressAfterDefaultPredecessor<K extends string>(
  order: readonly string[],
  accessorKeys: readonly K[],
): string[] {
  const addressKey = accessorKeys.find((key) =>
    PROPERTY_ADDRESS_COLUMN_KEYS.has(key),
  );
  if (!addressKey) return [...order];

  const defaultIndex = accessorKeys.indexOf(addressKey);
  const predecessor =
    defaultIndex > 0 ? accessorKeys[defaultIndex - 1] : undefined;
  if (!predecessor) return [...order];

  const predecessorIndex = order.indexOf(predecessor);
  if (predecessorIndex >= 0 && order[predecessorIndex + 1] === addressKey) {
    return [...order];
  }

  const next = [...order];
  const currentIndex = next.indexOf(addressKey);
  if (currentIndex >= 0) {
    next.splice(currentIndex, 1);
  }
  const insertAfter = next.indexOf(predecessor);
  const insertAt =
    insertAfter >= 0 ? insertAfter + 1 : Math.min(defaultIndex, next.length);
  next.splice(insertAt, 0, addressKey);
  return next;
}

/** localStorage prefix for a list table's saved layout. */
export function dashboardTableLayoutBaseKey(keyBase: string): string {
  return `dashboard-table-layout:${keyBase}`;
}

/**
 * Column order the list table displays for `accessorKeys`.
 * An empty saved order stays in accessor-key order. Until the address-layout
 * version is current, the property-address column is placed the same way
 * DashboardTable places it on load.
 */
export function resolveDisplayedColumnOrder<K extends string>(
  persistedOrder: readonly string[],
  addressColumnLayoutVersion: number,
  accessorKeys: readonly K[],
): string[] {
  const merged = mergeColumnOrderWithAccessorKeys(persistedOrder, accessorKeys);
  if (addressColumnLayoutVersion >= PROPERTY_ADDRESS_COLUMN_LAYOUT_VERSION) {
    return merged;
  }
  return placePropertyAddressAfterDefaultPredecessor(merged, accessorKeys);
}

function readDashboardLayoutJson<V>(key: string, fallback: V): V {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as V;
  } catch {
    return fallback;
  }
}

/** Read the column order DashboardTable persisted for this table. */
export function readDisplayedColumnOrder<K extends string>(
  tablePersistenceKey: string,
  accessorKeys: readonly K[],
): string[] {
  const base = dashboardTableLayoutBaseKey(tablePersistenceKey);
  const persisted = readDashboardLayoutJson<unknown>(`${base}:order`, []);
  const version = readDashboardLayoutJson<number>(
    `${base}:addressColumnLayoutVersion`,
    0,
  );
  return resolveDisplayedColumnOrder(
    Array.isArray(persisted)
      ? persisted.filter((key): key is string => typeof key === "string")
      : [],
    typeof version === "number" ? version : 0,
    accessorKeys,
  );
}

/** Which data columns to include: currently visible, or every configured column. */
export type ExportColumnMode = "visible" | "all";

/**
 * Resolves which accessor keys to use for an export. When `table` is omitted, pass
 * `columnVisibility` so "visible" mode can be computed (e.g. server or parent-driven exports).
 */
export function getExportAccessorKeysForMode<T, K extends string>(
  mode: ExportColumnMode,
  table: ReturnType<typeof useReactTable<T>> | null,
  accessorKeys: readonly K[],
  alwaysIncludeInExportKeys: readonly K[] | undefined,
  columnVisibility?: Record<K, boolean>,
): K[] {
  if (mode === "all") {
    const seen = new Set<K>(accessorKeys);
    const out: K[] = [...accessorKeys];
    for (const k of alwaysIncludeInExportKeys ?? []) {
      if (!seen.has(k)) {
        seen.add(k);
        out.push(k);
      }
    }
    return out;
  }
  if (table) {
    const visibleAccessorKeys = accessorKeys.filter(
      (key) => table.getColumn(key)?.getIsVisible() !== false,
    );
    const extraExportKeys = (alwaysIncludeInExportKeys ?? []).filter(
      (k) => !visibleAccessorKeys.includes(k),
    );
    return [...visibleAccessorKeys, ...extraExportKeys];
  }
  const visibleAccessorKeys = accessorKeys.filter(
    (key) => columnVisibility?.[key] !== false,
  );
  const extraExportKeys = (alwaysIncludeInExportKeys ?? []).filter(
    (k) => !visibleAccessorKeys.includes(k),
  );
  return [...visibleAccessorKeys, ...extraExportKeys];
}

/** Download pre-built string rows as CSV (shared with parent-driven exports). */
export function downloadTabularCsv(
  fileBasename: string,
  headers: string[],
  dataRows: string[][],
) {
  const csvData = [headers, ...dataRows];
  const csvContent = csvData
    .map((row) =>
      row
        .map((cell: string) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(","),
    )
    .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `${fileBasename}-${new Date().toISOString().split("T")[0]}.csv`,
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportTableRowsToCsv<T, K extends string>({
  rows,
  exportKeys,
  getColumnProperties,
  fileBasename = "data-export",
}: {
  rows: T[];
  exportKeys: readonly K[];
  getColumnProperties: (accessorKey: K) => ColumnProperties<T, K>;
  fileBasename?: string;
}) {
  const headers = exportKeys.map((key) => getColumnProperties(key).displayName);
  const dataRows = rows.map((row) =>
    exportKeys.map((key) => getColumnProperties(key).getValue(row, key)),
  );
  downloadTabularCsv(fileBasename, headers, dataRows);
}

export function exportTableRowsToExcel<T, K extends string>({
  rows,
  exportKeys,
  getColumnProperties,
  fileBasename = "data-export",
}: {
  rows: T[];
  exportKeys: readonly K[];
  getColumnProperties: (accessorKey: K) => ColumnProperties<T, K>;
  fileBasename?: string;
}) {
  const headers = exportKeys.map((key) => getColumnProperties(key).displayName);
  const dataRows = rows.map((row) =>
    exportKeys.map((key) => getColumnProperties(key).getValue(row, key)),
  );
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
  XLSX.utils.book_append_sheet(wb, ws, "Data");
  XLSX.writeFile(
    wb,
    `${fileBasename}-${new Date().toISOString().split("T")[0]}.xlsx`,
  );
}

function formatUpdatedAgo(dataUpdatedAt?: number): string | null {
  if (!dataUpdatedAt) return null;
  const seconds = Math.round((Date.now() - dataUpdatedAt) / 1000);
  if (seconds < 45) return "Updated just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `Updated ${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `Updated ${hours}h ago`;
}

// Export functions
function exportToCSV<T, K extends string>(
  table: ReturnType<typeof useReactTable<T>>,
  accessorKeys: readonly K[],
  getColumnProperties: (accessorKey: K) => ColumnProperties<T, K>,
  getExportColumnProperties:
    | ((accessorKey: K, mode: ExportColumnMode) => ColumnProperties<T, K>)
    | undefined,
  alwaysIncludeInExportKeys: readonly K[] | undefined,
  mode: ExportColumnMode,
) {
  const columnProps = (key: K) =>
    getExportColumnProperties?.(key, mode) ?? getColumnProperties(key);
  const exportAccessorKeys = getExportAccessorKeysForMode(
    mode,
    table,
    accessorKeys,
    alwaysIncludeInExportKeys,
  );

  const headers = exportAccessorKeys.map((key) => columnProps(key).displayName);

  // Get all filtered data rows
  const rows = table.getFilteredRowModel().rows.map((row) => {
    return exportAccessorKeys.map((key) =>
      columnProps(key).getValue(row.original, key),
    );
  });

  // Combine headers and rows
  const csvData = [headers, ...rows];

  // Convert to CSV string
  const csvContent = csvData
    .map((row) =>
      row
        .map((cell: string) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(","),
    )
    .join("\n");

  // Create and download file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `data-export-${new Date().toISOString().split("T")[0]}.csv`,
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function exportToExcel<T, K extends string>(
  table: ReturnType<typeof useReactTable<T>>,
  accessorKeys: readonly K[],
  getColumnProperties: (accessorKey: K) => ColumnProperties<T, K>,
  getExportColumnProperties:
    | ((accessorKey: K, mode: ExportColumnMode) => ColumnProperties<T, K>)
    | undefined,
  alwaysIncludeInExportKeys: readonly K[] | undefined,
  mode: ExportColumnMode,
) {
  const columnProps = (key: K) =>
    getExportColumnProperties?.(key, mode) ?? getColumnProperties(key);
  const exportAccessorKeys = getExportAccessorKeysForMode(
    mode,
    table,
    accessorKeys,
    alwaysIncludeInExportKeys,
  );

  const headers = exportAccessorKeys.map((key) => columnProps(key).displayName);

  // Get all filtered data rows
  const rows = table.getFilteredRowModel().rows.map((row) => {
    return exportAccessorKeys.map((key) =>
      columnProps(key).getValue(row.original, key),
    );
  });

  // Combine headers and rows
  const excelData = [headers, ...rows];

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(excelData);

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, "Data");

  // Generate and download file
  XLSX.writeFile(
    wb,
    `data-export-${new Date().toISOString().split("T")[0]}.xlsx`,
  );
}

// Reusable pagination component
function PaginationControls<T>({
  table,
  isFetching = false,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: {
  table: ReturnType<typeof useReactTable<T>>;
  isFetching?: boolean;
  pageSizeOptions?: readonly number[];
}) {
  return (
    <div className="flex items-center space-x-2">
      <div className="flex items-center space-x-2">
        <p className="text-sm font-medium">Rows per page</p>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="h-8 w-[70px]"
              disabled={isFetching}
            >
              {table.getState().pagination.pageSize}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {[...pageSizeOptions].map((pageSize) => (
              <DropdownMenuCheckboxItem
                key={pageSize}
                checked={table.getState().pagination.pageSize === pageSize}
                onCheckedChange={() => {
                  table.setPageSize(pageSize);
                }}
              >
                {pageSize}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex min-w-[100px] items-center justify-center gap-1 text-xs font-medium">
        {isFetching ? (
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        ) : null}
        Page {table.getState().pagination.pageIndex + 1} of{" "}
        {table.getPageCount()}
      </div>
      <div className="space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.previousPage()}
          disabled={isFetching || !table.getCanPreviousPage()}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.nextPage()}
          disabled={isFetching || !table.getCanNextPage()}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

/** Fixed-position filter panel — avoids Radix Presence loops (React 19 #185). */
function AnchoredFilterPanel({
  open,
  onOpenChange,
  triggerRef,
  children,
  className,
  align = "start",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  className?: string;
  align?: "start" | "end";
}) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState({ top: 0, left: 0 });
  const shiftUpRef = React.useRef(0);

  const updatePosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const panel = panelRef.current;
    const next = nextFilterPanelPosition({
      triggerRect: trigger.getBoundingClientRect(),
      panelWidth: panel?.offsetWidth || 320,
      panelHeight: panel?.offsetHeight ?? 0,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      align,
      previousShiftUp: shiftUpRef.current,
    });
    shiftUpRef.current = next.shiftUp;
    setPosition({ top: next.top, left: next.left });
  }, [align, triggerRef]);

  React.useLayoutEffect(() => {
    if (!open) {
      shiftUpRef.current = 0;
      return;
    }
    shiftUpRef.current = 0;
    updatePosition();
    const panel = panelRef.current;
    const resizeObserver =
      panel && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            updatePosition();
          })
        : null;
    if (resizeObserver && panel) {
      resizeObserver.observe(panel);
    }
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      onOpenChange(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onOpenChange, triggerRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      className={cn(
        "fixed z-[10000] rounded-md border bg-popover text-popover-foreground shadow-md outline-hidden",
        className,
      )}
      style={{ top: position.top, left: position.left }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}

function ColumnFilterPopover<T>({
  column,
  displayName,
  externalValues,
  open,
  onOpenChange,
  searchValue,
  onSearchChange,
  remoteValues = false,
  allowBlankFilter = true,
}: {
  column: Column<T, unknown>;
  displayName: string;
  /** When provided, replaces the facet-derived filter options with this list. */
  externalValues?: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Server facet endpoints return at most 500 suggestions per search. */
  remoteValues?: boolean;
  /** Hide Blank and Not blank when this column always has a value. */
  allowBlankFilter?: boolean;
}) {
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const filterValue = (column.getFilterValue() as string[]) ?? [];
  const hasActiveFilter = filterValue.length > 0;

  // Use faceted values as dependency so filter options update when table data
  // changes (e.g. after question list refetch following create/edit).
  const facetedValues = column.getFacetedUniqueValues();
  const distinctValues = React.useMemo(() => {
    const valuesByKey = new Map<string, string>();
    // When an external list is provided (e.g. all available license types from
    // the API), use it directly so options appear even when no rows match yet.
    const remember = (displayValue: string) => {
      if (
        displayValue === EMPTY_FILTER_VALUE ||
        displayValue === NOT_BLANK_FILTER_VALUE
      ) {
        return;
      }
      valuesByKey.set(
        displayValue.trim().replace(/\s+/g, " ").toLocaleLowerCase(),
        displayValue,
      );
    };
    if (externalValues && externalValues.length > 0) {
      for (const value of withoutPresenceFilterValues(externalValues)) {
        remember(value);
      }
      return [...valuesByKey.values()].sort((left, right) =>
        left.localeCompare(right, undefined, { sensitivity: "base" }),
      );
    }
    for (const [value] of facetedValues.entries()) {
      if (!value || String(value).trim() === "") continue;
      remember(String(value));
    }
    return [...valuesByKey.values()].sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: "base" }),
    );
  }, [facetedValues, externalValues]);

  const filteredValues = React.useMemo(() => {
    if (!searchValue) return distinctValues;
    return distinctValues.filter((value) =>
      value.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [distinctValues, searchValue]);

  const [parentRef, setParentRef] = React.useState<HTMLDivElement | null>(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredValues.length,
    getScrollElement: () => parentRef,
    estimateSize: () => 28, // Corresponds to the height of a CommandItem
    overscan: 5,
  });

  const handleValueToggle = (value: string) => {
    const newFilterValue = displayValueInList(filterValue, value)
      ? filterValue.filter((candidate) => !displayValuesEqual(candidate, value))
      : [...filterValue, value];
    column.setFilterValue(
      newFilterValue.length > 0 ? newFilterValue : undefined,
    );
  };

  const handleClearFilter = () => {
    column.setFilterValue(undefined);
    onOpenChange(false);
  };

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size="xs"
        aria-label={`Filter ${displayName}`}
        aria-expanded={open}
        className={`h-6 w-6 p-0 ${
          hasActiveFilter
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          onOpenChange(!open);
        }}
      >
        <Filter className="h-3 w-3" />
      </Button>
      <AnchoredFilterPanel
        open={open}
        onOpenChange={onOpenChange}
        triggerRef={triggerRef}
        className="w-80 p-0"
      >
        <Command shouldFilter={false}>
          <div className="flex items-center justify-between border-b pl-3.5 pr-2 py-2">
            <span className="text-sm font-medium text-muted-foreground">
              Filter {displayName}
            </span>
            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="xs"
                onClick={handleClearFilter}
                className="h-6 w-6 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          <FilterSearchInput
            placeholder="Search values..."
            value={searchValue}
            onValueChange={onSearchChange}
            focusOnMount
          />
          {remoteValues ? (
            <p className="border-b px-3 pb-2 text-xs text-muted-foreground">
              Showing up to 500 suggestions. Search to find additional values.
            </p>
          ) : null}
          {allowBlankFilter ? (
            <ColumnPresenceFilterChoices
              idPrefix={`value-filter-${column.id}`}
              values={filterValue}
              onToggle={handleValueToggle}
            />
          ) : null}
          <CommandList ref={setParentRef} className="max-h-64">
            {searchValue.trim() !== "" ? (
              <CommandGroup>
                <CommandItem
                  className="flex items-center gap-2 text-xs"
                  onMouseDown={(event) => event.preventDefault()}
                  onSelect={() =>
                    handleValueToggle(
                      encodeContainsFilterValue(searchValue.trim()),
                    )
                  }
                >
                  <span className="flex-1">Use “{searchValue.trim()}”</span>
                </CommandItem>
              </CommandGroup>
            ) : null}
            {rowVirtualizer.getVirtualItems().length > 0 ? (
              <CommandGroup
                className="relative"
                style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                  const value = filteredValues[virtualItem.index];
                  return (
                    <CommandItem
                      key={virtualItem.key}
                      className="absolute top-0 left-0 w-full flex items-center gap-2 text-xs"
                      style={{
                        height: `${virtualItem.size}px`,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                      onMouseDown={(event) => event.preventDefault()}
                      onSelect={() => handleValueToggle(value)}
                    >
                      <Checkbox
                        checked={displayValueInList(filterValue, value)}
                        onCheckedChange={() => handleValueToggle(value)}
                        onClick={(event) => event.stopPropagation()}
                      />
                      <span className="flex-1">{value}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : (
              <CommandEmpty>No results found.</CommandEmpty>
            )}
          </CommandList>
          {hasActiveFilter && (
            <div className="border-t p-2">
              <Button
                variant="link"
                size="xs"
                onClick={handleClearFilter}
                className="w-full justify-start text-xs p-0 h-auto"
              >
                Clear filter
              </Button>
            </div>
          )}
        </Command>
      </AnchoredFilterPanel>
    </>
  );
}

function ColumnDateRangeFilterPopover<T>({
  column,
  displayName,
  allowBlankFilter = true,
}: {
  column: Column<T, unknown>;
  displayName: string;
  /** Hide Blank and Not blank when this date is always set. */
  allowBlankFilter?: boolean;
}) {
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);
  const filterValue = (column.getFilterValue() as string[] | undefined) ?? [];
  const bounds = React.useMemo(
    () => parseDateRangeFilterValue(filterValue),
    [filterValue],
  );
  const fromDate = bounds.from ? parseDateAsLocal(bounds.from) : null;
  const toDate = bounds.to ? parseDateAsLocal(bounds.to) : null;
  const hasActiveFilter = Boolean(
    bounds.from || bounds.to || bounds.includeBlank || bounds.includeNotBlank,
  );
  const blankCheckboxId = `date-filter-blank-${column.id}`;
  const notBlankCheckboxId = `date-filter-not-blank-${column.id}`;

  const updateBounds = React.useCallback(
    (
      nextFrom: string | null,
      nextTo: string | null,
      nextIncludeBlank = Boolean(bounds.includeBlank),
      nextIncludeNotBlank = Boolean(bounds.includeNotBlank),
    ) => {
      column.setFilterValue(
        buildDateRangeFilterValue(
          nextFrom,
          nextTo,
          nextIncludeBlank,
          nextIncludeNotBlank,
        ),
      );
    },
    [bounds.includeBlank, bounds.includeNotBlank, column],
  );

  const handleFromChange = (date: Date | null) => {
    updateBounds(date ? format(date, "yyyy-MM-dd") : null, bounds.to);
  };

  const handleToChange = (date: Date | null) => {
    updateBounds(bounds.from, date ? format(date, "yyyy-MM-dd") : null);
  };

  const handleClearFilter = () => {
    column.setFilterValue(undefined);
    setOpen(false);
  };

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size="xs"
        aria-label={`Filter ${displayName}`}
        aria-expanded={open}
        className={`h-6 w-6 p-0 ${
          hasActiveFilter
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        <Filter className="h-3 w-3" />
      </Button>
      <AnchoredFilterPanel
        open={open}
        onOpenChange={setOpen}
        triggerRef={triggerRef}
        className="w-80 p-3"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-muted-foreground">
            Filter {displayName}
          </span>
          {hasActiveFilter && (
            <Button
              variant="ghost"
              size="xs"
              onClick={handleClearFilter}
              className="h-6 w-6 p-0"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        <div className="space-y-3">
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">
              From
            </span>
            <DatePicker
              value={fromDate}
              onChange={handleFromChange}
              placeholder="Any date"
            />
          </div>
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">
              To
            </span>
            <DatePicker
              value={toDate}
              onChange={handleToChange}
              placeholder="Any date"
            />
          </div>
          {allowBlankFilter ? (
            <>
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id={blankCheckboxId}
                  checked={Boolean(bounds.includeBlank)}
                  onCheckedChange={(checked) =>
                    updateBounds(bounds.from, bounds.to, checked === true)
                  }
                />
                <label
                  htmlFor={blankCheckboxId}
                  className="text-xs font-medium text-muted-foreground cursor-pointer"
                >
                  Blank
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id={notBlankCheckboxId}
                  checked={Boolean(bounds.includeNotBlank)}
                  onCheckedChange={(checked) =>
                    updateBounds(
                      bounds.from,
                      bounds.to,
                      bounds.includeBlank,
                      checked === true,
                    )
                  }
                />
                <label
                  htmlFor={notBlankCheckboxId}
                  className="text-xs font-medium text-muted-foreground cursor-pointer"
                >
                  Not blank
                </label>
              </div>
            </>
          ) : null}
        </div>
        {hasActiveFilter && (
          <Button
            variant="link"
            size="xs"
            onClick={handleClearFilter}
            className="mt-3 w-full justify-start text-xs p-0 h-auto"
          >
            Clear filter
          </Button>
        )}
      </AnchoredFilterPanel>
    </>
  );
}
// Generic table component
export function DashboardTable<T extends object, K extends string>({
  data,
  onRowClick,
  columnVisibility: externalColumnVisibility,
  onColumnVisibilityChange,
  columnFilters: externalColumnFilters,
  onColumnFiltersChange,
  defaultColumnFilters,
  accessorKeys,
  getColumnProperties,
  getExportColumnProperties,
  getRowId,
  initialSortColumn,
  initialSortDirection,
  enableSorting = true,
  enableColumnFilters = true,
  renderRowLeadingActions,
  leadingActionsHeaderLabel = "",
  renderRowActions,
  rowActionsColumnWidth = ROW_ACTIONS_COLUMN_WIDTH,
  customControls,
  leadingToolbarControls,
  footerLeadingControls,
  optimizeForStreaming = false,
  alwaysIncludeInExportKeys,
  tablePersistenceKey,
  serverMode = false,
  rowCount,
  pageIndex,
  pageSize: pageSizeProp,
  onPaginationChange,
  sorting: sortingProp,
  onSortingChange: onSortingChangeProp,
  lockToViewport = true,
  hideTopPagination = false,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  leadingContent,
  onRefresh,
  isRefreshing = false,
  refreshAriaLabel = "Refresh",
  dataUpdatedAt,
  onFilterPopoverOpen,
  onServerExport,
  onVisibleRowsChange,
}: DashboardTableProps<T, K>) {
  const DEFAULT_COLUMN_MIN_WIDTH = 96;
  const DEFAULT_COLUMN_MAX_WIDTH = 560;
  const DEFAULT_COLUMN_PADDING_AND_CONTROLS_WIDTH = 64;
  const APPROX_TEXT_CHAR_WIDTH_PX = 7;

  const resolvedTablePersistenceKey = React.useMemo(() => {
    const path =
      typeof window !== "undefined" ? window.location.pathname : "no-window";
    const keyBase = tablePersistenceKey ?? path;
    return dashboardTableLayoutBaseKey(keyBase);
  }, [tablePersistenceKey]);

  const visibilityStorageKey = `${resolvedTablePersistenceKey}:visibility`;
  const orderStorageKey = `${resolvedTablePersistenceKey}:order`;
  const addressColumnLayoutVersionKey = `${resolvedTablePersistenceKey}:addressColumnLayoutVersion`;
  const sizingStorageKey = `${resolvedTablePersistenceKey}:sizing`;
  const pageSizeStorageKey = `${resolvedTablePersistenceKey}:pageSize`;
  const filtersStorageKey = `${resolvedTablePersistenceKey}:filters`;

  const readLocalStorageJson = React.useCallback(
    <V,>(key: string, fallback: V): V => {
      if (typeof window === "undefined") return fallback;
      try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return fallback;
        return JSON.parse(raw) as V;
      } catch {
        return fallback;
      }
    },
    [],
  );

  const writeLocalStorageJson = React.useCallback(
    <V,>(key: string, value: V) => {
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Ignore storage quota or serialization errors.
      }
    },
    [],
  );

  const readSessionStorageJson = React.useCallback(
    <V,>(key: string, fallback: V): V => {
      if (typeof window === "undefined") return fallback;
      try {
        const raw = window.sessionStorage.getItem(key);
        if (!raw) return fallback;
        return JSON.parse(raw) as V;
      } catch {
        return fallback;
      }
    },
    [],
  );

  const writeSessionStorageJson = React.useCallback(
    <V,>(key: string, value: V) => {
      if (typeof window === "undefined") return;
      try {
        window.sessionStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Ignore storage quota or serialization errors.
      }
    },
    [],
  );

  const [sorting, setSorting] = React.useState<SortingState>(
    initialSortColumn && initialSortDirection
      ? [
          {
            id: initialSortColumn,
            desc: initialSortDirection === "desc",
          },
        ]
      : [],
  );
  const sortingState = serverMode && sortingProp ? sortingProp : sorting;
  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    if (serverMode && onSortingChangeProp) {
      onSortingChangeProp(updater);
      return;
    }
    setSorting(updater);
  };

  const [columnFiltersState, setColumnFiltersState] =
    React.useState<ColumnFiltersState>(() => {
      const externalEntries = Object.entries(externalColumnFilters ?? {})
        .filter(([, values]) => Array.isArray(values) && values.length > 0)
        .map(([id, values]) => ({ id, value: values as string[] }));
      if (externalEntries.length > 0) return externalEntries;
      const persisted = readSessionStorageJson<ColumnFiltersState>(
        filtersStorageKey,
        [],
      );
      const hasPersistedFilters =
        typeof window !== "undefined" &&
        window.sessionStorage.getItem(filtersStorageKey) !== null;
      if (hasPersistedFilters) return persisted;
      return Object.entries(defaultColumnFilters ?? {})
        .filter(([, values]) => Array.isArray(values) && values.length > 0)
        .map(([id, values]) => ({ id, value: values as string[] }));
    });
  const persistedFiltersToHydrateRef = React.useRef(columnFiltersState);
  const hasHydratedPersistedFiltersRef = React.useRef(false);
  const [openFilterColumnId, setOpenFilterColumnId] = React.useState<
    string | null
  >(null);
  const [filterSearchByColumn, setFilterSearchByColumn] = React.useState<
    Record<string, string>
  >({});
  const filterPopoverStateRef = React.useRef({
    openId: null as string | null,
    search: {} as Record<string, string>,
  });
  filterPopoverStateRef.current = {
    openId: openFilterColumnId,
    search: filterSearchByColumn,
  };
  const openFilterColumnIdRef = React.useRef(openFilterColumnId);
  openFilterColumnIdRef.current = openFilterColumnId;
  const filterSearchByColumnRef = React.useRef(filterSearchByColumn);
  filterSearchByColumnRef.current = filterSearchByColumn;
  const openFilterSearch =
    openFilterColumnId == null
      ? ""
      : (filterSearchByColumn[openFilterColumnId] ?? "");
  const lastOpenedFilterColumnIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!openFilterColumnId || !onFilterPopoverOpen) {
      lastOpenedFilterColumnIdRef.current = null;
      return;
    }
    const search = openFilterSearch.trim();
    const openedNow =
      lastOpenedFilterColumnIdRef.current !== openFilterColumnId;
    lastOpenedFilterColumnIdRef.current = openFilterColumnId;
    if (openedNow) {
      onFilterPopoverOpen(openFilterColumnId, search || undefined);
      return;
    }
    const timer = window.setTimeout(() => {
      onFilterPopoverOpen(openFilterColumnId, search || undefined);
    }, FILTER_VALUE_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [openFilterColumnId, openFilterSearch, onFilterPopoverOpen]);
  const [draggingColumnId, setDraggingColumnId] = React.useState<string | null>(
    null,
  );
  const [headerContextMenu, setHeaderContextMenu] = React.useState<{
    x: number;
    y: number;
    columnId: string;
    displayName: string;
  } | null>(null);

  // Update column filters when external filters change
  React.useEffect(() => {
    if (externalColumnFilters === undefined) {
      return;
    }

    const newColumnFilters = Object.entries(externalColumnFilters)
      .filter(([_, values]) => Array.isArray(values) && values.length > 0)
      .map(([id, values]) => ({ id, value: values as string[] }));

    if (!hasHydratedPersistedFiltersRef.current) {
      hasHydratedPersistedFiltersRef.current = true;
      if (
        newColumnFilters.length === 0 &&
        persistedFiltersToHydrateRef.current.length > 0 &&
        onColumnFiltersChange
      ) {
        const restoredFilters = persistedFiltersToHydrateRef.current.reduce(
          (acc, filter) => {
            acc[filter.id as K] = filter.value as string[];
            return acc;
          },
          {} as Record<K, string[]>,
        );
        onColumnFiltersChange(restoredFilters);
        return;
      }
    }

    setColumnFiltersState((prev) => {
      if (columnFiltersStateEqual(prev, newColumnFilters)) {
        return prev;
      }
      return newColumnFilters;
    });
  }, [externalColumnFilters, onColumnFiltersChange]);

  React.useEffect(() => {
    writeSessionStorageJson(filtersStorageKey, columnFiltersState);
  }, [columnFiltersState, filtersStorageKey, writeSessionStorageJson]);

  const [internalColumnVisibility, setInternalColumnVisibility] =
    React.useState<VisibilityState>(
      (() => {
        const defaults = accessorKeys.reduce((acc, key) => {
          acc[key] = getColumnProperties(key).defaultVisible;
          return acc;
        }, {} as VisibilityState);
        const persisted = readLocalStorageJson<VisibilityState>(
          visibilityStorageKey,
          {},
        );
        return { ...defaults, ...persisted };
      })(),
    );

  const addressColumnLayoutVersionRef = React.useRef(
    readLocalStorageJson<number>(addressColumnLayoutVersionKey, 0),
  );

  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(() => {
    const persisted = readLocalStorageJson<ColumnOrderState>(
      orderStorageKey,
      [],
    );
    return resolveDisplayedColumnOrder(
      Array.isArray(persisted) ? persisted : [],
      addressColumnLayoutVersionRef.current,
      accessorKeys,
    );
  });

  // Keep widths for unknown ids too: dynamic columns (e.g. question-backed
  // license-list columns) register after mount and would otherwise lose
  // their persisted size on every load.
  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>(
    () => readLocalStorageJson<ColumnSizingState>(sizingStorageKey, {}),
  );
  const [pagination, setPagination] = React.useState<PaginationState>(() => {
    const persistedPageSize = readLocalStorageJson<number>(
      pageSizeStorageKey,
      50,
    );
    const pageSize =
      Number.isInteger(persistedPageSize) && persistedPageSize > 0
        ? persistedPageSize
        : 50;
    return {
      pageIndex: 0,
      pageSize,
    };
  });
  const paginationState: PaginationState = serverMode
    ? {
        pageIndex: pageIndex ?? 0,
        pageSize: pageSizeProp ?? pagination.pageSize,
      }
    : pagination;
  const handlePaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next =
      typeof updater === "function" ? updater(paginationState) : updater;
    if (serverMode) {
      onPaginationChange?.(next);
      return;
    }
    setPagination(next);
  };

  // Use the correct row type for columnHelper
  const columnHelper = React.useMemo(() => createColumnHelper<T>(), []);

  const estimatedDefaultColumnWidths = React.useMemo(() => {
    const SAMPLE_ROW_LIMIT = 500;
    const sampledRows =
      data.length > SAMPLE_ROW_LIMIT ? data.slice(0, SAMPLE_ROW_LIMIT) : data;
    return accessorKeys.reduce(
      (acc, key) => {
        const props = getColumnProperties(key);
        if (props.defaultWidth != null) {
          acc[key] = props.defaultWidth;
          return acc;
        }
        let maxContentLength = props.displayName.length;
        for (const row of sampledRows) {
          const value = props.getValue(row, key);
          const normalizedValue = String(value ?? "");
          if (normalizedValue.length > maxContentLength) {
            maxContentLength = normalizedValue.length;
          }
        }
        const estimatedWidth =
          maxContentLength * APPROX_TEXT_CHAR_WIDTH_PX +
          DEFAULT_COLUMN_PADDING_AND_CONTROLS_WIDTH;
        acc[key] = Math.min(
          DEFAULT_COLUMN_MAX_WIDTH,
          Math.max(DEFAULT_COLUMN_MIN_WIDTH, estimatedWidth),
        );
        return acc;
      },
      {} as Record<K, number>,
    );
  }, [accessorKeys, data, getColumnProperties]);

  const getColumnPropertiesRef = React.useRef(getColumnProperties);
  getColumnPropertiesRef.current = getColumnProperties;

  // Type-safe filter function
  const arrIncludesSome: FilterFn<T> = (row, columnId, filterValue) => {
    if (!Array.isArray(filterValue)) return true;
    const columnProps = getColumnPropertiesRef.current(columnId as K);
    if (columnProps.rowMatchesFilter) {
      return columnProps.rowMatchesFilter(row.original, filterValue);
    }
    const cellValue = row.getValue(columnId) as string;
    // Convert empty values to "(Empty)" for comparison
    const displayValue =
      !cellValue || cellValue.trim() === "" ? EMPTY_FILTER_VALUE : cellValue;
    return displayValueMatchesFilters(displayValue, filterValue);
  };

  // Use external column visibility if provided, otherwise use internal
  const columnVisibility = React.useMemo(() => {
    // Start with default visibility from getColumnProperties
    const defaultVisibility = accessorKeys.reduce((acc, key) => {
      acc[key] = getColumnProperties(key).defaultVisible;
      return acc;
    }, {} as VisibilityState);

    // If external visibility is provided with values, merge it with defaults
    if (
      externalColumnVisibility &&
      Object.keys(externalColumnVisibility).length > 0
    ) {
      return Object.entries(externalColumnVisibility).reduce(
        (acc, [key, value]) => {
          acc[key] = Boolean(value);
          return acc;
        },
        { ...defaultVisibility, ...internalColumnVisibility },
      );
    }

    // Otherwise use internal visibility (which also starts with defaults)
    return internalColumnVisibility;
  }, [
    externalColumnVisibility,
    internalColumnVisibility,
    accessorKeys,
    getColumnProperties,
  ]);

  const setColumnVisibility = React.useCallback(
    (
      updaterOrValue:
        | VisibilityState
        | ((old: VisibilityState) => VisibilityState),
    ) => {
      const newVisibility =
        typeof updaterOrValue === "function"
          ? updaterOrValue(columnVisibility)
          : updaterOrValue;

      setInternalColumnVisibility(newVisibility);
      writeLocalStorageJson(visibilityStorageKey, newVisibility);
      if (onColumnVisibilityChange) {
        // Convert VisibilityState to Record<K, boolean> for external use
        const externalVisibility = Object.entries(newVisibility).reduce(
          (acc, [key, value]) => {
            acc[key as K] = Boolean(value);
            return acc;
          },
          {} as Record<K, boolean>,
        );
        onColumnVisibilityChange(externalVisibility);
      }
    },
    [
      onColumnVisibilityChange,
      columnVisibility,
      writeLocalStorageJson,
      visibilityStorageKey,
    ],
  );

  const clearFilterForColumn = React.useCallback(
    (columnId: string) => {
      setColumnFiltersState((prev) => {
        const next = prev.filter((f) => f.id !== columnId);
        if (next.length === prev.length) {
          return prev;
        }
        if (onColumnFiltersChange) {
          const externalFilters = next.reduce(
            (acc: Record<K, string[]>, filter) => {
              acc[filter.id as K] = filter.value as string[];
              return acc;
            },
            {} as Record<K, string[]>,
          );
          onColumnFiltersChange(externalFilters);
        }
        return next;
      });
    },
    [onColumnFiltersChange],
  );

  const setColumnHidden = React.useCallback(
    (columnId: string, hidden: boolean) => {
      setColumnVisibility((prev) => ({
        ...prev,
        [columnId]: !hidden,
      }));
      if (hidden) {
        clearFilterForColumn(columnId);
      }
    },
    [setColumnVisibility, clearFilterForColumn],
  );

  const startColumnDrag = React.useCallback((columnId: string) => {
    setDraggingColumnId(columnId);
  }, []);

  const endColumnDrag = React.useCallback(() => {
    setDraggingColumnId(null);
  }, []);

  React.useEffect(() => {
    writeLocalStorageJson(visibilityStorageKey, internalColumnVisibility);
  }, [internalColumnVisibility, visibilityStorageKey, writeLocalStorageJson]);

  React.useEffect(() => {
    writeLocalStorageJson(orderStorageKey, columnOrder);
  }, [columnOrder, orderStorageKey, writeLocalStorageJson]);

  React.useEffect(() => {
    writeLocalStorageJson(sizingStorageKey, columnSizing);
  }, [columnSizing, sizingStorageKey, writeLocalStorageJson]);

  React.useEffect(() => {
    writeLocalStorageJson(pageSizeStorageKey, paginationState.pageSize);
  }, [paginationState.pageSize, pageSizeStorageKey, writeLocalStorageJson]);

  React.useEffect(() => {
    const layoutVersion = addressColumnLayoutVersionRef.current;
    setColumnOrder((prev) => {
      const next = resolveDisplayedColumnOrder(
        prev,
        layoutVersion,
        accessorKeys,
      );
      return sameStringOrder(prev, next) ? prev : next;
    });
    if (
      layoutVersion < PROPERTY_ADDRESS_COLUMN_LAYOUT_VERSION &&
      accessorKeys.some((key) => PROPERTY_ADDRESS_COLUMN_KEYS.has(key))
    ) {
      addressColumnLayoutVersionRef.current =
        PROPERTY_ADDRESS_COLUMN_LAYOUT_VERSION;
      writeLocalStorageJson(
        addressColumnLayoutVersionKey,
        PROPERTY_ADDRESS_COLUMN_LAYOUT_VERSION,
      );
    }
  }, [accessorKeys, addressColumnLayoutVersionKey, writeLocalStorageJson]);

  React.useEffect(() => {
    if (!headerContextMenu) return;
    const close = () => setHeaderContextMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("contextmenu", close);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("contextmenu", close);
      window.removeEventListener("resize", close);
    };
  }, [headerContextMenu]);

  // Keep column defs stable across facet/filter-driven getColumnProperties
  // identity changes. Recreating columns remounts open filter panels and
  // trips React 19 Presence maximum-update-depth (#185) when a list filter is
  // applied on large bulk tables.
  const estimatedDefaultColumnWidthsRef = React.useRef(
    estimatedDefaultColumnWidths,
  );
  estimatedDefaultColumnWidthsRef.current = estimatedDefaultColumnWidths;
  const remoteFilterValuesRef = React.useRef(false);
  remoteFilterValuesRef.current = serverMode && Boolean(onFilterPopoverOpen);

  const columns = React.useMemo((): ColumnDef<T>[] => {
    return accessorKeys.map((key) => {
      const initialProps = getColumnPropertiesRef.current(key);
      const filterType = resolveColumnFilterType(key, initialProps.filterType);
      const isDateRangeFilter = filterType === "dateRange";

      return columnHelper.accessor(
        (row) => getColumnPropertiesRef.current(key).getValue(row, key),
        {
          id: key,
          size: estimatedDefaultColumnWidthsRef.current[key],
          minSize: DEFAULT_COLUMN_MIN_WIDTH,
          header: ({ column }) => {
            const columnProps = getColumnPropertiesRef.current(key);
            const filterable =
              enableColumnFilters && columnProps.filterable !== false;
            return (
              <div className="flex min-w-0 items-center gap-1">
                {enableSorting ? (
                  <Button
                    variant="ghost"
                    size="xs"
                    className="min-w-0 max-w-full px-1"
                    onClick={() => column.toggleSorting()}
                  >
                    <span className="truncate">{columnProps.displayName}</span>
                    {column.getIsSorted() === "asc" ? (
                      <ArrowUp className="h-4 w-4 shrink-0" />
                    ) : column.getIsSorted() === "desc" ? (
                      <ArrowDown className="h-4 w-4 shrink-0" />
                    ) : null}
                  </Button>
                ) : (
                  <span className="px-2 text-sm font-medium truncate">
                    {columnProps.displayName}
                  </span>
                )}
                {filterable &&
                  !column.getIsResizing() &&
                  (isDateRangeFilter ? (
                    <ColumnDateRangeFilterPopover
                      column={column}
                      displayName={columnProps.displayName}
                      allowBlankFilter={columnProps.allowBlankFilter !== false}
                    />
                  ) : (
                    <ColumnFilterPopover
                      column={column}
                      displayName={columnProps.displayName}
                      externalValues={columnProps.filterValues}
                      allowBlankFilter={columnProps.allowBlankFilter !== false}
                      open={openFilterColumnIdRef.current === key}
                      onOpenChange={(next) => {
                        setOpenFilterColumnId(next ? key : null);
                        if (!next) {
                          setFilterSearchByColumn((prev) => {
                            if (!(key in prev)) return prev;
                            const nextSearch = { ...prev };
                            delete nextSearch[key];
                            return nextSearch;
                          });
                        }
                      }}
                      searchValue={filterSearchByColumnRef.current[key] ?? ""}
                      remoteValues={remoteFilterValuesRef.current}
                      onSearchChange={(value) =>
                        setFilterSearchByColumn((prev) => ({
                          ...prev,
                          [key]: value,
                        }))
                      }
                    />
                  ))}
              </div>
            );
          },
          cell: ({ getValue, row }) => {
            const columnProps = getColumnPropertiesRef.current(key);
            if (columnProps.renderCell) {
              return (
                <div className="w-full max-w-full truncate font-medium">
                  {columnProps.renderCell(row.original)}
                </div>
              );
            }
            return (
              <div className="w-full max-w-full truncate font-medium">
                {getValue()}
              </div>
            );
          },
          ...(enableColumnFilters &&
            initialProps.filterable !== false &&
            (isDateRangeFilter
              ? {
                  filterFn: (
                    row: Row<T>,
                    _columnId: string,
                    filterValue: unknown,
                  ) => {
                    if (
                      !Array.isArray(filterValue) ||
                      filterValue.length === 0
                    ) {
                      return true;
                    }
                    const columnProps = getColumnPropertiesRef.current(key);
                    const raw = columnProps.getFilterValue
                      ? columnProps.getFilterValue(row.original, key)
                      : defaultDateFilterValue(
                          row.original,
                          key,
                          columnProps.getValue,
                        );
                    return matchesDateRangeFilter(
                      raw,
                      parseDateRangeFilterValue(filterValue as string[]),
                    );
                  },
                }
              : {
                  filterFn: "arrIncludesSome",
                })),
          enableColumnFilter:
            enableColumnFilters && initialProps.filterable !== false,
          enableSorting,
          ...(initialProps.sortingFn && {
            sortingFn: initialProps.sortingFn as SortingFn<T>,
          }),
        },
      ) as ColumnDef<T>;
    });
  }, [accessorKeys, columnHelper, enableSorting, enableColumnFilters]);

  const skipClientRowModels = optimizeForStreaming || serverMode;
  const table = useReactTable({
    data,
    columns,
    enableSorting,
    enableColumnFilters,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: (updater) => {
      const newColumnFilters =
        typeof updater === "function" ? updater(columnFiltersState) : updater;
      const unchanged = columnFiltersStateEqual(
        newColumnFilters,
        columnFiltersState,
      );
      if (unchanged) return;
      // Keep the panel open so additional values can be selected. Column defs
      // are memoized to avoid the React 19 remount loop (#185) that previously
      // required closing here.
      setColumnFiltersState(newColumnFilters);
      if (onColumnFiltersChange) {
        // Convert internal format back to external format
        const externalFilters = newColumnFilters.reduce(
          (acc: Record<K, string[]>, filter) => {
            acc[filter.id as K] = filter.value as string[];
            return acc;
          },
          {} as Record<K, string[]>,
        );
        onColumnFiltersChange(externalFilters);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getFacetedRowModel:
      enableColumnFilters && !skipClientRowModels
        ? getFacetedRowModel()
        : undefined,
    getFacetedUniqueValues:
      enableColumnFilters && !skipClientRowModels
        ? getFacetedUniqueValues()
        : undefined,
    getPaginationRowModel: serverMode ? undefined : getPaginationRowModel(),
    getSortedRowModel:
      enableSorting && !skipClientRowModels ? getSortedRowModel() : undefined,
    getFilteredRowModel:
      enableColumnFilters && !skipClientRowModels
        ? getFilteredRowModel()
        : undefined,
    manualPagination: serverMode,
    manualSorting: serverMode,
    manualFiltering: serverMode,
    pageCount: serverMode
      ? Math.max(
          1,
          Math.ceil((rowCount ?? 0) / Math.max(1, paginationState.pageSize)),
        )
      : undefined,
    rowCount: serverMode ? rowCount : undefined,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onPaginationChange: handlePaginationChange,
    columnResizeMode: "onChange",
    enableColumnResizing: true,
    filterFns: {
      arrIncludesSome,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: paginationState.pageSize,
      },
    },
    state: {
      sorting: sortingState,
      columnFilters: columnFiltersState,
      pagination: paginationState,
      columnVisibility,
      columnOrder,
      columnSizing,
    },
    autoResetAll: false,
    autoResetPageIndex: false,
    autoResetExpanded: false,
  });

  const visibleNavRowIds = table
    .getPrePaginationRowModel()
    .rows.map((row) => row.id)
    .join("\0");

  // biome-ignore lint/correctness/useExhaustiveDependencies: table identity changes every render; visibleNavRowIds captures filter/sort/data
  React.useEffect(() => {
    if (!onVisibleRowsChange) {
      return;
    }
    onVisibleRowsChange(
      table.getPrePaginationRowModel().rows.map((row) => row.original),
    );
  }, [data, onVisibleRowsChange, visibleNavRowIds]);

  const activeColumnFilterBadges = React.useMemo(() => {
    const externalFilters = columnFiltersState.reduce(
      (acc: Record<K, string[]>, filter) => {
        acc[filter.id as K] = filter.value as string[];
        return acc;
      },
      {} as Record<K, string[]>,
    );
    return buildColumnFilterBadges(
      externalFilters,
      accessorKeys,
      getColumnPropertiesRef.current,
    );
  }, [columnFiltersState, accessorKeys]);
  const stickyFilterBarRef = React.useRef<HTMLDivElement>(null);
  const [stickyFilterBarHeight, setStickyFilterBarHeight] = React.useState(0);

  React.useLayoutEffect(() => {
    const element = stickyFilterBarRef.current;
    if (!element || activeColumnFilterBadges.length === 0) {
      setStickyFilterBarHeight(0);
      return;
    }
    const updateHeight = () => setStickyFilterBarHeight(element.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [activeColumnFilterBadges.length]);

  const clearAllColumnFilters = React.useCallback(() => {
    setColumnFiltersState([]);
    onColumnFiltersChange?.({} as Record<K, string[]>);
  }, [onColumnFiltersChange]);

  const removeColumnFilter = React.useCallback(
    (badge: ColumnFilterBadge) => {
      const currentFilters = columnFiltersState.reduce(
        (acc: Record<string, string[]>, filter) => {
          acc[filter.id] = filter.value as string[];
          return acc;
        },
        {},
      );
      const nextFilters = removeColumnFilterBadge(currentFilters, badge);
      setColumnFiltersState(
        Object.entries(nextFilters).map(([id, value]) => ({ id, value })),
      );
      onColumnFiltersChange?.(nextFilters as Record<K, string[]>);
    },
    [columnFiltersState, onColumnFiltersChange],
  );

  const [isExporting, setIsExporting] = React.useState(false);
  const isExportingRef = React.useRef(false);

  const runExport = React.useCallback(
    async (mode: ExportColumnMode, format: "csv" | "excel") => {
      if (isExportingRef.current) return;
      isExportingRef.current = true;
      setIsExporting(true);
      const generatingToastId = toast({
        title: "Generating export…",
        description: "Large lists can take a while.",
      });
      try {
        if (onServerExport) {
          await onServerExport(mode, format);
          return;
        }
        if (format === "csv") {
          exportToCSV(
            table,
            accessorKeys,
            getColumnProperties,
            getExportColumnProperties,
            alwaysIncludeInExportKeys,
            mode,
          );
        } else {
          exportToExcel(
            table,
            accessorKeys,
            getColumnProperties,
            getExportColumnProperties,
            alwaysIncludeInExportKeys,
            mode,
          );
        }
      } catch (error) {
        toast({
          title: "Export failed",
          description: getQueryErrorMessage(error),
          variant: "destructive",
        });
      } finally {
        sonnerToast.dismiss(generatingToastId);
        isExportingRef.current = false;
        setIsExporting(false);
      }
    },
    [
      accessorKeys,
      alwaysIncludeInExportKeys,
      getColumnProperties,
      getExportColumnProperties,
      onServerExport,
      table,
    ],
  );

  const totalTableWidth =
    table.getTotalSize() +
    (renderRowLeadingActions ? LEADING_ACTIONS_COLUMN_WIDTH : 0) +
    (renderRowActions ? rowActionsColumnWidth : 0);

  const listClipRef = React.useRef<HTMLDivElement>(null);
  const hBarRef = React.useRef<HTMLDivElement>(null);
  const hScrollSyncRef = React.useRef(false);
  const hScrollLeftRef = React.useRef(0);
  const hOverflowRef = React.useRef(0);
  const [hScrollLeft, setHScrollLeft] = React.useState(0);
  const [listClipWidth, setListClipWidth] = React.useState(0);
  const hOverflow = Math.max(0, totalTableWidth - listClipWidth);
  hScrollLeftRef.current = hScrollLeft;
  hOverflowRef.current = hOverflow;

  const applyHorizontalScroll = React.useCallback((nextLeft: number) => {
    const clamped = Math.max(0, Math.min(hOverflowRef.current, nextLeft));
    hScrollLeftRef.current = clamped;
    setHScrollLeft(clamped);
    const bar = hBarRef.current;
    if (bar && bar.scrollLeft !== clamped) {
      hScrollSyncRef.current = true;
      bar.scrollLeft = clamped;
      hScrollSyncRef.current = false;
    }
  }, []);

  React.useLayoutEffect(() => {
    if (!lockToViewport) return;
    const el = listClipRef.current;
    if (!el) return;
    const updateWidth = () => setListClipWidth(el.clientWidth);
    updateWidth();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, [lockToViewport]);

  React.useLayoutEffect(() => {
    if (hScrollLeft > hOverflow) applyHorizontalScroll(hOverflow);
  }, [applyHorizontalScroll, hOverflow, hScrollLeft]);

  React.useEffect(() => {
    if (!lockToViewport) return;
    const el = listClipRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      const dx =
        event.shiftKey && Math.abs(event.deltaX) < Math.abs(event.deltaY)
          ? event.deltaY
          : event.deltaX;
      if (dx === 0 || hOverflowRef.current <= 0) return;
      event.preventDefault();
      applyHorizontalScroll(hScrollLeftRef.current + dx);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [applyHorizontalScroll, lockToViewport]);

  const scrollListHorizontallyBy = React.useCallback(
    (dx: number) => {
      const el = listClipRef.current;
      if (!el) return;
      if (lockToViewport) {
        applyHorizontalScroll(hScrollLeftRef.current + dx);
        return;
      }
      el.scrollLeft += dx;
    },
    [applyHorizontalScroll, lockToViewport],
  );

  const columnOrderRef = React.useRef(columnOrder);
  columnOrderRef.current = columnOrder;

  // One visible slot per call. The pointer must cross the neighbor's midpoint
  // first; swapping on any overlap jumps uneven columns and then snaps back.
  const stepColumnDrag = React.useCallback(
    (clientX: number): boolean => {
      if (!draggingColumnId) return false;
      const list = listClipRef.current;
      if (!list) return false;
      const headers = leafColumnHeaderCells(list).flatMap((el) => {
        const id = el.dataset.columnId;
        if (!id) return [];
        const rect = el.getBoundingClientRect();
        return [{ id, left: rect.left, width: rect.width }];
      });
      const neighborId = columnDragNeighborId(
        headers,
        draggingColumnId,
        clientX,
      );
      if (!neighborId) return false;
      const visibleIds = headers.map((header) => header.id);
      const current = columnOrderRef.current.length
        ? columnOrderRef.current
        : accessorKeys;
      const next = reorderColumnByVisibleNeighbor(
        current,
        visibleIds,
        draggingColumnId,
        neighborId,
      );
      if (sameStringOrder(current, next)) return false;
      columnOrderRef.current = next;
      setColumnOrder(next);
      return true;
    },
    [accessorKeys, draggingColumnId],
  );

  // Held in refs so the auto-scroll loop below only tears down when the drag
  // ends, not on every re-render the drag itself causes.
  const scrollListHorizontallyByRef = React.useRef(scrollListHorizontallyBy);
  scrollListHorizontallyByRef.current = scrollListHorizontallyBy;
  const stepColumnDragRef = React.useRef(stepColumnDrag);
  stepColumnDragRef.current = stepColumnDrag;

  // Dragging a column header towards (or past) either edge of the list scrolls
  // horizontally, so a column can be dropped on a target that starts off-screen.
  React.useEffect(() => {
    if (!draggingColumnId) return;
    const el = listClipRef.current;
    if (!el) return;

    let frame = 0;
    let catchUpFrame = 0;
    let velocity = 0;
    let pointerX = 0;
    let pointerY = 0;

    const cancelCatchUp = () => {
      if (catchUpFrame) cancelAnimationFrame(catchUpFrame);
      catchUpFrame = 0;
    };
    // A fast drag can cross several midpoints before the row repaints.
    // Catch up one column per frame after layout, instead of leaping.
    const scheduleCatchUp = () => {
      if (catchUpFrame !== 0) return;
      catchUpFrame = requestAnimationFrame(() => {
        catchUpFrame = 0;
        if (stepColumnDragRef.current(pointerX)) scheduleCatchUp();
      });
    };

    const step = () => {
      if (velocity === 0) {
        frame = 0;
        return;
      }
      scrollListHorizontallyByRef.current(velocity);
      // The pointer is stationary while the list scrolls under it, so
      // dragover does not fire. Swap as neighbor midpoints cross it.
      if (stepColumnDragRef.current(pointerX)) scheduleCatchUp();
      frame = requestAnimationFrame(step);
    };

    const stop = () => {
      velocity = 0;
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      cancelCatchUp();
    };

    const onDragOver = (event: DragEvent) => {
      pointerX = event.clientX;
      pointerY = event.clientY;
      if (stepColumnDragRef.current(pointerX)) scheduleCatchUp();
      const rect = el.getBoundingClientRect();
      const withinList =
        pointerY >= rect.top - COLUMN_DRAG_EDGE_ZONE_PX &&
        pointerY <= rect.bottom + COLUMN_DRAG_EDGE_ZONE_PX;
      const leftDepth = rect.left + COLUMN_DRAG_EDGE_ZONE_PX - pointerX;
      const rightDepth = pointerX - (rect.right - COLUMN_DRAG_EDGE_ZONE_PX);
      const speed = (depth: number) =>
        Math.min(1, depth / COLUMN_DRAG_EDGE_ZONE_PX) *
        COLUMN_DRAG_MAX_SCROLL_PX_PER_FRAME;

      if (!withinList) velocity = 0;
      else if (leftDepth > 0) velocity = -speed(leftDepth);
      else if (rightDepth > 0) velocity = speed(rightDepth);
      else velocity = 0;

      if (velocity !== 0 && frame === 0) frame = requestAnimationFrame(step);
    };

    document.addEventListener("dragover", onDragOver);
    document.addEventListener("dragend", stop);
    document.addEventListener("drop", stop);
    return () => {
      stop();
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("dragend", stop);
      document.removeEventListener("drop", stop);
    };
  }, [draggingColumnId]);

  return (
    <div
      className={cn(
        "w-full",
        lockToViewport && "flex min-h-0 flex-1 flex-col overflow-hidden",
      )}
      aria-busy={isRefreshing || undefined}
    >
      <div
        data-slot="dashboard-table-scroll"
        className={cn(
          lockToViewport &&
            "min-h-0 flex-1 overflow-x-clip overflow-y-auto overscroll-contain",
        )}
      >
        <div className={cn(lockToViewport && "w-full bg-background")}>
          {leadingContent}
          <div
            className={cn(
              "mb-2 flex items-center gap-4",
              hideTopPagination ? "justify-end" : "justify-between",
            )}
          >
            {hideTopPagination ? null : (
              <PaginationControls
                table={table}
                isFetching={isRefreshing}
                pageSizeOptions={pageSizeOptions}
              />
            )}
            <div className="flex items-center gap-2">
              {onRefresh ? (
                <div className="flex items-center gap-2">
                  {formatUpdatedAgo(dataUpdatedAt) ? (
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatUpdatedAgo(dataUpdatedAt)}
                    </span>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label={refreshAriaLabel}
                    title={refreshAriaLabel}
                    disabled={isRefreshing}
                    onClick={() => onRefresh()}
                  >
                    <RefreshCw
                      className={cn("h-4 w-4", isRefreshing && "animate-spin")}
                    />
                  </Button>
                </div>
              ) : null}
              {leadingToolbarControls}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="ml-auto"
                    disabled={isExporting}
                    aria-busy={isExporting}
                  >
                    {isExporting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating export…
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-4 w-4" />
                        Export <ChevronDown className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    disabled={isExporting}
                    onClick={() => void runExport("visible", "csv")}
                  >
                    CSV — visible columns
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={isExporting}
                    onClick={() => void runExport("all", "csv")}
                  >
                    CSV — all columns
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={isExporting}
                    onClick={() => void runExport("visible", "excel")}
                  >
                    Excel — visible columns
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={isExporting}
                    onClick={() => void runExport("all", "excel")}
                  >
                    Excel — all columns
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="ml-auto">
                    <Columns3 className="mr-2 h-4 w-4" />
                    Columns <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {(columnOrder.length > 0 ? columnOrder : accessorKeys)
                    .map((columnId) => table.getColumn(columnId))
                    .filter((column): column is NonNullable<typeof column> => {
                      if (!column) return false;
                      return column.getCanHide();
                    })
                    .map((column) => {
                      return (
                        <div
                          key={column.id}
                          className="flex items-center gap-2 px-2 py-1.5"
                        >
                          <Checkbox
                            id={`column-visible-${column.id}`}
                            checked={column.getIsVisible()}
                            onCheckedChange={(value) => {
                              const isVisible = Boolean(value);
                              setColumnHidden(column.id, !isVisible);
                            }}
                          />
                          <label
                            htmlFor={`column-visible-${column.id}`}
                            className="text-sm flex-1"
                          >
                            {getColumnProperties(column.id as K).displayName}
                          </label>
                        </div>
                      );
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
              {customControls}
            </div>
          </div>
        </div>
        {activeColumnFilterBadges.length > 0 ? (
          <div
            ref={stickyFilterBarRef}
            data-slot="dashboard-table-active-filters"
            className={cn(
              "bg-background pb-2",
              lockToViewport && "sticky -top-px z-30",
            )}
          >
            <DashboardColumnFilterBadges
              badges={activeColumnFilterBadges}
              onClearAll={clearAllColumnFilters}
              onRemove={removeColumnFilter}
              className="mb-0"
            />
          </div>
        ) : null}
        <div
          ref={listClipRef}
          data-slot="dashboard-table-list"
          className={cn(
            "relative bg-card text-card-foreground",
            lockToViewport ? "overflow-x-clip" : "overflow-x-auto",
          )}
        >
          <ListLoadingOverlay show={isRefreshing} />
          <table
            className="rounded-md min-w-full border divide divide-border border-collapse table-fixed"
            style={{
              width: totalTableWidth,
              marginLeft:
                lockToViewport && hScrollLeft > 0 ? -hScrollLeft : undefined,
            }}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {renderRowLeadingActions && (
                    <th
                      className={cn(
                        "p-1 text-left text-xs border bg-muted text-card-foreground align-middle",
                        lockToViewport && "sticky z-20",
                      )}
                      style={{
                        top: lockToViewport
                          ? stickyFilterBarHeight - 1
                          : undefined,
                        width: LEADING_ACTIONS_COLUMN_WIDTH,
                        minWidth: LEADING_ACTIONS_COLUMN_WIDTH,
                        maxWidth: LEADING_ACTIONS_COLUMN_WIDTH,
                      }}
                    >
                      <span className="sr-only">
                        {leadingActionsHeaderLabel || "Row actions"}
                      </span>
                    </th>
                  )}
                  {headerGroup.headers.map((header) => {
                    const headerDisplayName = header.isPlaceholder
                      ? "Column"
                      : getColumnProperties(header.id as K).displayName;
                    return (
                      <th
                        key={header.id}
                        data-column-id={header.id}
                        className={cn(
                          "relative p-2 text-left text-xs border bg-muted text-card-foreground",
                          // -1px hides the sub-pixel seam that lets row content
                          // peek above the pinned header on fractional scrolls.
                          lockToViewport && "sticky z-20",
                        )}
                        style={{
                          top: lockToViewport
                            ? stickyFilterBarHeight - 1
                            : undefined,
                          width: header.getSize(),
                          minWidth: header.getSize(),
                          maxWidth: header.getSize(),
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          endColumnDrag();
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setHeaderContextMenu({
                            x: e.clientX,
                            y: e.clientY,
                            columnId: header.id,
                            displayName: headerDisplayName,
                          });
                        }}
                      >
                        <div className="w-full min-w-0 flex items-center gap-1">
                          <button
                            type="button"
                            className="inline-flex h-4 w-4 shrink-0 items-center justify-center cursor-grab active:cursor-grabbing"
                            draggable={!header.isPlaceholder}
                            onDragStart={() => startColumnDrag(header.id)}
                            onDragEnd={endColumnDrag}
                            onClick={(e) => e.preventDefault()}
                            aria-label={`Reorder ${headerDisplayName} column`}
                            title={`Reorder ${headerDisplayName}`}
                          >
                            <GripVertical className="h-3 w-3 opacity-60" />
                          </button>
                          <div className="min-w-0 flex-1">
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </div>
                        </div>
                        {header.column.getCanResize() && (
                          <div
                            role="separator"
                            aria-orientation="vertical"
                            tabIndex={0}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              header.getResizeHandler()(e);
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                              header.getResizeHandler()(e);
                            }}
                            className={cn(
                              "absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none touch-none",
                              header.column.getIsResizing()
                                ? "bg-primary"
                                : "hover:bg-primary/50",
                            )}
                          />
                        )}
                      </th>
                    );
                  })}
                  {renderRowActions && (
                    <th
                      className={cn(
                        STICKY_ACTIONS_HEADER_CLASS,
                        lockToViewport && "z-40",
                      )}
                      style={{
                        top: lockToViewport
                          ? stickyFilterBarHeight - 1
                          : undefined,
                        width: rowActionsColumnWidth,
                        minWidth: rowActionsColumnWidth,
                        maxWidth: rowActionsColumnWidth,
                      }}
                    >
                      Actions
                    </th>
                  )}
                </tr>
              ))}
            </thead>
            <tbody className="divide divide-border">
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={cn(
                      onRowClick ? "cursor-pointer hover:bg-muted/50" : "",
                      "is_deleted" in row.original &&
                        row.original.is_deleted === true
                        ? "opacity-50"
                        : "",
                      "group",
                    )}
                    onClick={() => onRowClick?.(getRowId(row.original))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick?.(getRowId(row.original));
                      }
                    }}
                    tabIndex={onRowClick ? 0 : undefined}
                    role={onRowClick ? "button" : undefined}
                  >
                    {renderRowLeadingActions && (
                      <td
                        className="w-12 min-w-[3rem] px-1 py-2 text-xs border-r align-middle"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {renderRowLeadingActions(row.original)}
                      </td>
                    )}
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-3.5 py-2 text-xs border-r whitespace-nowrap overflow-hidden"
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                        }}
                      >
                        <div className="w-full max-w-full min-w-0 overflow-hidden truncate">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      </td>
                    ))}
                    {renderRowActions && (
                      <td
                        className={STICKY_ACTIONS_CELL_CLASS}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{
                          width: rowActionsColumnWidth,
                          minWidth: rowActionsColumnWidth,
                          maxWidth: rowActionsColumnWidth,
                        }}
                      >
                        {renderRowActions(row.original)}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={
                      columns.length +
                      (renderRowLeadingActions ? 1 : 0) +
                      (renderRowActions ? 1 : 0)
                    }
                    className="h-24 text-sm text-center"
                  >
                    No results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {headerContextMenu ? (
        <div
          className="fixed z-50 min-w-48 rounded-md border bg-popover text-popover-foreground shadow-md p-1"
          style={{
            top: headerContextMenu.y,
            left: headerContextMenu.x,
          }}
        >
          <button
            type="button"
            className="w-full text-left text-sm px-2 py-1.5 rounded-sm hover:bg-accent hover:text-accent-foreground"
            onClick={() => {
              setColumnHidden(headerContextMenu.columnId, true);
              setHeaderContextMenu(null);
            }}
          >
            Hide {headerContextMenu.displayName}
          </button>
        </div>
      ) : null}
      <div
        data-slot="dashboard-table-footer"
        className="relative shrink-0 border-t bg-background"
      >
        {lockToViewport ? (
          <div
            ref={hBarRef}
            data-slot="dashboard-table-h-scroll"
            className={cn(
              "absolute inset-x-0 bottom-full z-20 h-auto min-h-4 overflow-x-auto overflow-y-hidden",
              hOverflow <= 0 && "hidden",
            )}
            aria-label="Scroll list horizontally"
            onScroll={(event) => {
              if (hScrollSyncRef.current) return;
              applyHorizontalScroll(event.currentTarget.scrollLeft);
            }}
          >
            <div style={{ width: totalTableWidth, height: 1 }} />
          </div>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-4">
          <div className="flex-1 text-sm text-muted-foreground">
            {(() => {
              const filteredRows = serverMode
                ? (rowCount ?? 0)
                : table.getFilteredRowModel().rows.length;
              const pageIndex = table.getState().pagination.pageIndex;
              const pageSize = table.getState().pagination.pageSize;
              const startRow = pageIndex * pageSize + 1;
              const endRow = Math.min((pageIndex + 1) * pageSize, filteredRows);

              if (filteredRows === 0) {
                return "No rows to display.";
              }

              return `Showing rows ${startRow}-${endRow} of ${filteredRows}`;
            })()}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {footerLeadingControls}
            <PaginationControls
              table={table}
              isFetching={isRefreshing}
              pageSizeOptions={pageSizeOptions}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Flex wrapper so DashboardTable can fill remaining portal viewport height. */
export function DashboardListSection({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("flex min-h-0 flex-1 flex-col min-w-0", className)}>
      {children}
    </div>
  );
}
